import clsx from 'clsx'
import { isArray, noop } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { OverlayTrigger, Popover, Tooltip } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { Badge } from '../../../components/Badge'
import { Button } from '../../../components/Button'
import { ExternalLink } from '../../../components/ExternalLink'
import { Table } from '../../../components/Table'
import { AUGMENTT_POSTURE_STATES, COMPLIANCE_TYPES, MICROSOFT_POSTURE_STATES, SHORTCUTS } from '../constants'
import postureConfig from '../postureConfig'
import {
  createOptions,
  getPostureColorCode,
  getPostureName,
  getPostureRationale,
  getPostureStatusDetails,
  getPostureStatusMessage,
  sortSecurityChecks,
} from '../utils'
import ErrorPopover from './ErrorPopover'
import './SecurityPostureTable.module.scss'
import SecureScore from '../SecureScore/SecureScore'

const propTypes = {
  /**
   * Array of postures
   */
  tableData: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  /**
   * Defines if table is loading
   */
  isLoading: PropTypes.bool,
  /**
   * Callback called when row is clicked, with params: row
   */
  onRowClick: PropTypes.func,
  /**
   * Callback called when the defender info icon is clicked, with params: row
   */
  onDefenderInfoClick: PropTypes.func,
  /**
   * Callback called when an resolution menu option is clicked, with params: { securityCheck, initialStatus, show }
   */
  openResolutionModal: PropTypes.func,
  /**
   * If true, table will be rendered hiding all interactive elements for a printable report
   */
  print: PropTypes.bool,
  /**
   *  If true, table with be rendered hiding checkboxes and three dot menu
   */
  readOnly: PropTypes.bool,
  /**
   * Callback to get the table controls passed to the table
   */
  getTableControls: PropTypes.func,
  /**
   * Indicates if user is licensed for policy management
   */
  isPolicyManagementEnabled: PropTypes.bool,
  /**
   * Icon of the cap configure button
   */
  capIcon: PropTypes.node.isRequired,
  /**
   * Icon of the defender configure button
   */
  defenderIcon: PropTypes.node.isRequired,

  afterFilter: PropTypes.func,
}

const preFormatComplianceFilter = (row, intl) => {
  const compliance = []

  const isComplianceFunction = typeof postureConfig?.[row.checkId]?.compliance === 'function'

  const compliances = isComplianceFunction
    ? postureConfig?.[row.checkId]?.compliance({ row, intl })
    : postureConfig?.[row.checkId]?.compliance

  // eslint-disable-next-line no-restricted-syntax
  for (const complianceType of Object.values(COMPLIANCE_TYPES)) {
    const hasComplianceType = compliances?.[complianceType]
    if (hasComplianceType) {
      compliance.push(complianceType)
    }
  }

  return compliance
}

/**
 * Security Posture Table
 */
function SecurityPostureTable(props) {
  const {
    tableData = [],
    isLoading = false,
    onRowClick = noop,
    onDefenderInfoClick = noop,
    openResolutionModal = noop,
    print = false,
    readOnly = true,
    getTableControls = undefined,
    isPolicyManagementEnabled = false,
    afterFilter,
    // passing capIcon and defenderIcon as props, after spending too much time trying to make them work in both report generator and the frontend
    capIcon,
    defenderIcon,
  } = props ?? {}

  const intl = useIntl()

  const formattedData = useMemo(() => {
    if (!tableData?.length) {
      return []
    }
    return sortSecurityChecks(tableData, intl).map((item) => ({
      ...item,
      // format columns beforehand so filter/sort works
      formattedComplianceFilter: preFormatComplianceFilter(item, intl),
      formattedStatus: getPostureStatusMessage(item, intl),
      formattedName: getPostureName(item.checkId, intl),
    }))
  }, [tableData, intl])

  const licenseOptions = useMemo(() => createOptions(formattedData, 'license'), [formattedData])

  const complianceOptions = useMemo(() => createOptions(formattedData, 'formattedComplianceFilter'), [formattedData])

  // Formats the security checks column
  const formatNameColumn = (cell, row) => (
    <span className="Uic-SecurityPostureTable-securityCheckCell">{row.formattedName}</span>
  )

  // Formats the Status column
  const formatStatusColumn = (cell, row) => (
    <>
      <div>
        <Badge
          variant={getPostureColorCode(row)}
          className={clsx('Uic-SecurityPostureTable-complianceBadge', 'me-2')}
          id={`${row.checkId}-status`}
        >
          {getPostureStatusMessage(row, intl)}
        </Badge>
        <ErrorPopover error={row.error} />
      </div>
      <div>{getPostureStatusDetails(row, intl)}</div>
    </>
  )

  const getConfigurationIcon = ({ row, shortcut, isPolicyConfigurationBlocked }) => {
    const hasConfigurationIssues =
      row?.configurationIssues?.missingScopesInIntegration.length > 0 ||
      row?.configurationIssues?.missingGDAPInIntegration.length > 0 ||
      isPolicyConfigurationBlocked

    if (shortcut === SHORTCUTS.CAP) {
      return (
        <div
          className={clsx(
            'Uic-SecurityPostureTable-svgWrapper',
            hasConfigurationIssues && 'Uic-SecurityPostureTable-grayout',
          )}
        >
          {capIcon || <i className="fas fa-key" />}
        </div>
      )
    }

    if (shortcut === SHORTCUTS.DEFENDER) {
      return (
        <div
          className={clsx(
            'Uic-SecurityPostureTable-svgWrapper',
            hasConfigurationIssues && 'Uic-SecurityPostureTable-grayout',
          )}
        >
          {defenderIcon || <i className="fas fa-user-shield" />}
        </div>
      )
    }

    return (
      <i
        title={intl.formatMessage({ id: 'SECURITY_CHECK_TABLE.CONFIGURE' })}
        className="fas fa-gear"
        style={{
          color: hasConfigurationIssues ? 'var(--aug-text-icon-muted)' : 'var(--aug-text-emphasis)',
        }}
      />
    )
  }

  const formatConfigureShortcut = (row, shortcut) => {
    const isPolicyConfigurationBlocked =
      (shortcut === SHORTCUTS.CAP || shortcut === SHORTCUTS.DEFENDER) && !isPolicyManagementEnabled

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        onClick={(e) => {
          e.stopPropagation()
          if (shortcut === SHORTCUTS.DEFENDER) {
            onDefenderInfoClick(row)
          } else if (!isPolicyConfigurationBlocked) {
            onRowClick(row, shortcut)
          }
        }}
      >
        <OverlayTrigger
          show={isPolicyConfigurationBlocked ? undefined : false}
          overlay={
            <Tooltip>
              <FormattedMessage id="SECURITY_CHECK_TABLE.UNLICENSED_FOR_POLICY_MANAGEMENT" />
            </Tooltip>
          }
        >
          {getConfigurationIcon({
            row,
            shortcut,
            isPolicyConfigurationBlocked,
          })}
        </OverlayTrigger>
      </div>
    )
  }

  const formatInfoShortcut = (row) => (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={(e) => {
        e.stopPropagation()
        onRowClick(row, 'info')
      }}
    >
      <i
        title={intl.formatMessage({
          id: 'SECURITY_CHECK_TABLE.INSTRUCTIONS',
        })}
        className="fas fa-info"
      />
    </div>
  )

  // Format the config/alert status
  const formatConfigureAlertColumn = (cell, row) => {
    let shortcut = postureConfig?.[row.checkId]?.shortcut

    if (!shortcut) {
      return null
    }

    if (!isArray(shortcut)) {
      shortcut = [shortcut]
    }

    return (
      <div className={shortcut.length > 1 ? 'Uic-SecurityPostureTable-multiShortcut' : ''}>
        {shortcut.map((type) => {
          if (type !== SHORTCUTS.INSTRUCTIONS) {
            return formatConfigureShortcut(row, type)
          }
          return formatInfoShortcut(row)
        })}
      </div>
    )
  }

  const formatExtraRoleColumn = (cell, row) => {
    const { configurationIssues } = row
    if (
      !configurationIssues ||
      (configurationIssues?.missingScopesInIntegration?.length === 0 &&
        configurationIssues?.missingGDAPInIntegration?.length === 0)
    ) {
      return null
    }

    // eslint-disable-next-line no-shadow
    const renderTooltip = (props) => (
      <Popover className="Uic-SecurityPostureTable-scopePopUpWidth" {...props}>
        <div className="Uic-SecurityPostureTable-extraScope">
          <div className="d-flex">
            <div className="alert-icon alert-icon-top">
              <i
                title="Scope missing"
                className={`${'Uic-SecurityPostureTable-fontWarning'} fa fa-triangle-exclamation `}
              />
            </div>
            <div className="fw-bold ms-1">
              {' '}
              {row.configurationIssues.missingScopesInIntegration.length > 0 ? (
                <FormattedMessage id="SECURE.POSTURE_MISSING_SCOPE" />
              ) : (
                <FormattedMessage id="SECURE.POSTURE_MISSING_ROLES" />
              )}
            </div>
          </div>
          <div>
            {row.configurationIssues.missingScopesInIntegration.length > 0 ? (
              <FormattedMessage id="SECURE.POSTURE_MISSING_SCOPE_DESCRIPTION" />
            ) : (
              <FormattedMessage id="SECURE.POSTURE_MISSING_ROLE_DESCRIPTION" />
            )}
            <ul>
              {row.configurationIssues.missingScopesInIntegration.length > 0
                ? row.configurationIssues.missingScopesInIntegration.map((scope) => <li key={scope}>{scope}</li>)
                : row.configurationIssues.missingGDAPInIntegration.map((scope) => <li key={scope}>{scope}</li>)}
            </ul>
          </div>
          <Button
            onClick={() => {
              row.onScopeClick()
            }}
          >
            {row.configurationIssues.missingScopesInIntegration.length > 0 ? (
              <FormattedMessage id="SECURE.REAUTHENTICATE" />
            ) : (
              <FormattedMessage id="SECURE.MANAGE_CUSTOMER" />
            )}
          </Button>
          <br />
          &nbsp;
          {row.configurationIssues.missingScopesInIntegration.length > 0 && (
            <ExternalLink
              href="https://kb.augmentt.com/kb/guide/en/m365-integration-permissions-oIdxVm07dR/Steps/2915607"
              label="More Information"
            />
          )}
        </div>
      </Popover>
    )

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
        }}
        className="float-end"
      >
        <OverlayTrigger trigger="click" rootClose placement="left" overlay={renderTooltip}>
          <div className="alert-icon alert-icon-top">
            <i className={`${'Uic-SecurityPostureTable-fontWarning'} fa fa-triangle-exclamation `} />
          </div>
        </OverlayTrigger>
      </div>
    )
  }

  const formatRationaleColumn = (cell, row) => getPostureRationale(row, intl)

  const formatComplianceColumn = (cell, row) => {
    const renderArray = []

    const isComplianceFunction = typeof postureConfig?.[row.checkId]?.compliance === 'function'

    const compliances = isComplianceFunction
      ? postureConfig?.[row.checkId]?.compliance({ row, intl })
      : postureConfig?.[row.checkId]?.compliance

    Object.values(COMPLIANCE_TYPES).forEach((complianceType, index) => {
      const complianceNumbers = compliances?.[complianceType]

      if (complianceNumbers) {
        renderArray.push(
          <div key={complianceType} className={clsx(index !== 0 && 'mt-1')}>
            {complianceType}
            &nbsp;({complianceNumbers.join(', ')})
          </div>,
        )
      }
    })

    return renderArray
  }

  const formatSecureScoreColumn = (cell, row) => <SecureScore {...row.msSecureScore} />

  const showFilter = (filterConfig) => (!print ? filterConfig : null)

  const columns = [
    {
      dataField: 'formattedName',
      text: intl.formatMessage({ id: 'SECURITY_CHECK_TABLE.SECURITY_CHECK' }),
      formatter: formatNameColumn,
      filter: showFilter({
        type: Table.FILTER_TYPES.TEXT,
      }),
      sort: !print,
      headerStyle: () => ({ width: '14%' }),
    },
    {
      dataField: 'formattedStatus',
      text: intl.formatMessage({ id: 'SECURITY_CHECK_TABLE.STATUS' }),
      formatter: formatStatusColumn,
      sort: !print,
      headerStyle: () => ({ width: '18%' }),
    },
    {
      dataField: 'license',
      text: intl.formatMessage({ id: 'SECURITY_CHECK_TABLE.LICENSE' }),
      filter: showFilter({
        type: Table.FILTER_TYPES.MULTISELECT,
        options: licenseOptions,
      }),
      sort: !print,
      headerStyle: () => ({ width: '12%' }),
    },
    {
      dataField: 'rationale',
      text: intl.formatMessage({ id: 'SECURITY_CHECK_TABLE.RATIONALE' }),
      formatter: formatRationaleColumn,
      headerStyle: () => ({ width: '33%' }),
    },
    {
      dataField: 'formattedComplianceFilter',
      text: intl.formatMessage({ id: 'SECURITY_CHECK_TABLE.COMPLIANCE' }),
      formatter: formatComplianceColumn,
      filter: showFilter({
        type: Table.FILTER_TYPES.MULTISELECT,
        options: complianceOptions,
      }),
      headerStyle: () => ({ width: '9%' }),
    },
    {
      dataField: 'msSecureScore.currentScore',
      text: intl.formatMessage({ id: 'SECURITY_CHECK_TABLE.SECURE_SCORE' }),
      tooltip: !print
        ? {
            message: <FormattedMessage id="SECURE.POSTURE.SECURE_SCORE_DELAY_WARNING" />,
          }
        : null,
      formatter: formatSecureScoreColumn,
      sort: !print,
      headerStyle: () => ({ width: '11%' }),
    },
    {
      dataField: 'configurationIssues',
      text: '',
      formatter: formatExtraRoleColumn,
      headerStyle: () => ({ width: '3%' }),
    },
    {
      dataField: 'shortcut',
      text: '',
      formatter: formatConfigureAlertColumn,
      headerStyle: () => ({ width: '2%' }),
    },
  ].filter((column) => !((print || readOnly) && column.dataField === 'shortcut'))

  // eslint-disable-next-line react/function-component-definition, react/no-unstable-nested-components
  const formatMenuLabel = (messageId, state) => (row) => {
    const formatted = <FormattedMessage id={messageId} />
    // eslint-disable-next-line react/destructuring-assignment
    if (row.msSecureScore?.scoreControlState === state) {
      return (
        <span className="fw-bold">
          {formatted}
          <i className={clsx('fas fa-check ms-2', 'Uic-SecurityPostureTable-checkIcon')} />
        </span>
      )
    }
    return formatted
  }

  const onOpenResolutionModal = (selectedStatus) => (row) => {
    openResolutionModal({
      securityCheck: row,
      initialStatus: selectedStatus,
      show: true,
    })
  }

  const isMenuDisabled = (row) => row?.status === AUGMENTT_POSTURE_STATES.COMPLIANT

  const menuDisableLabel = intl.formatMessage({
    id: 'SECURE.NO_CHANGE_COMPLIANT',
  })

  const menu = [
    {
      label: formatMenuLabel('SECURITY_CHECK.STATE.AUGMENTT_COMPLIANCE', MICROSOFT_POSTURE_STATES.DEFAULT),
      onClick: onOpenResolutionModal(MICROSOFT_POSTURE_STATES.DEFAULT),
      isDisabled: isMenuDisabled,
      disableLabel: menuDisableLabel,
    },
    {
      label: formatMenuLabel('SECURITY_CHECK.STATE.RISK_ACCEPTED', MICROSOFT_POSTURE_STATES.RISK_ACCEPTED),
      onClick: onOpenResolutionModal(MICROSOFT_POSTURE_STATES.RISK_ACCEPTED),
      isDisabled: isMenuDisabled,
      disableLabel: menuDisableLabel,
    },
    {
      label: formatMenuLabel('SECURITY_CHECK.STATE.THIRD_PARTY', MICROSOFT_POSTURE_STATES.THIRD_PARTY),
      onClick: onOpenResolutionModal(MICROSOFT_POSTURE_STATES.THIRD_PARTY),
      isDisabled: isMenuDisabled,
      disableLabel: menuDisableLabel,
    },
    {
      label: formatMenuLabel(
        'SECURITY_CHECK.STATE.ALTERNATE_MITIGATION',
        MICROSOFT_POSTURE_STATES.ALTERNATE_MITIGATION,
      ),
      onClick: onOpenResolutionModal(MICROSOFT_POSTURE_STATES.ALTERNATE_MITIGATION),
      isDisabled: isMenuDisabled,
      disableLabel: menuDisableLabel,
    },
  ]

  const showMenu = useCallback((row) => row?.msSecureScore?.scoreControlState, [])

  const rowEvents = {
    onClick: (e, row) => {
      onRowClick(row)
    },
  }

  return (
    <div className="Uic-SecurityPostureTable-tableWrapper">
      <Table
        afterFilter={afterFilter}
        data={formattedData}
        keyField="id"
        columns={columns}
        menu={!readOnly ? menu : null}
        showMenu={showMenu}
        loading={isLoading}
        rowEvents={rowEvents}
        hideHeader
        hidePagination
        readOnly={print || readOnly}
        getTableControls={getTableControls}
      />
    </div>
  )
}

SecurityPostureTable.propTypes = propTypes

export default SecurityPostureTable
